<template>
    <div ref="mapContainer" class="map-container"></div>
</template>

<script>
const DIDI_LOADER_URL = 'https://lbs.xiaojukeji.com/api/v2/static/loader.js';
const DEFAULT_DIDI_KEY = 'ba1ff739119329d56376aed173930938';
const DEFAULT_DIDI_SECURITY_CODE = '7fd85bf918f123ab20a399aa78c02e43';
const DEFAULT_CENTER = [116.397428, 39.90923];

let diMapLoaderPromise = null;

export default {
    props: {
        center: {
            type: [Array, Object],
            default: () => DEFAULT_CENTER
        },
        zoom: {
            type: Number,
            default: 11
        },
        onMapLoad: {
            type: Function,
            default: () => {}
        },
        mapType: {
            type: String,
            default: 'position'
        }
    },
    data() {
        return {
            didiMapData: null,
            map: null,
            DiMap: null,
            mapLoaded: false,
            mapLoading: false,
            pendingCenter: null,
            pendingPaths: [],
            currentMarkers: [],
            currentPolylines: []
        };
    },
    mounted() {
        setTimeout(() => {
            const center = this.normalizeCenter(this.center);
            this.initMap(center);
        }, 100);
    },
    beforeDestroy() {
        this.clearOverlays();
        if (this.map && this.map.destroy) {
            this.map.destroy();
        }
        this.map = null;
        this.DiMap = null;
        this.mapLoaded = false;
    },
    methods: {
        getDidiKey() {
            return process.env.VUE_APP_DIDI_MAP_KEY || DEFAULT_DIDI_KEY;
        },
        getDidiSecurityCode() {
            return process.env.VUE_APP_DIDI_MAP_SECURITY_CODE || DEFAULT_DIDI_SECURITY_CODE;
        },
        validateDidiConfig() {
            const key = this.getDidiKey();
            const securityCode = this.getDidiSecurityCode();

            if (!key) {
                throw new Error('Missing VUE_APP_DIDI_MAP_KEY');
            }
            if (!securityCode) {
                throw new Error('Missing VUE_APP_DIDI_MAP_SECURITY_CODE');
            }
            return { key, securityCode };
        },
        normalizeCenter(val) {
            let mapLng;
            let mapLat;
            if (Array.isArray(val)) {
                mapLng = val[0];
                mapLat = val[1];
                const point = this.wgs84ToGcj02(mapLng, mapLat);
                this.didiMapData = point;
                return [point.lng, point.lat];
            } else if (val && val.bdLon && val.bdLat) {
                const point = this.bd09ToGcj02(val.bdLon, val.bdLat);
                this.didiMapData = point;
                return [point.lng, point.lat];
            } else if (val && val.lastDataLongitude && val.lastDataLatitude) {
                mapLng = val.lastDataLongitude;
                mapLat = val.lastDataLatitude;
            } else if (val && val.lng && val.lat) {
                mapLng = val.lng;
                mapLat = val.lat;
            } else {
                mapLng = DEFAULT_CENTER[0];
                mapLat = DEFAULT_CENTER[1];
            }
            const point = this.wgs84ToGcj02(mapLng, mapLat);
            this.didiMapData = point;
            return [point.lng, point.lat];
        },
        ensureDiMapLoader() {
            if (window.DiMapLoader && window.DiMapLoader.load) {
                return Promise.resolve(window.DiMapLoader);
            }
            if (diMapLoaderPromise) {
                return diMapLoaderPromise;
            }
            const { securityCode } = this.validateDidiConfig();
            window._DiMapSecurityConfig = Object.assign({}, window._DiMapSecurityConfig || {}, {
                securityJsCode: securityCode
            });
            diMapLoaderPromise = new Promise((resolve, reject) => {
                const existed = document.querySelector('script[data-dimap-loader="true"]');
                if (existed) {
                    existed.addEventListener('load', () => resolve(window.DiMapLoader));
                    existed.addEventListener('error', () => reject(new Error('滴滴地图 Loader 加载失败')));
                    return;
                }
                const script = document.createElement('script');
                script.src = DIDI_LOADER_URL;
                script.async = true;
                script.setAttribute('data-dimap-loader', 'true');
                script.onload = () => {
                    if (window.DiMapLoader && window.DiMapLoader.load) {
                        resolve(window.DiMapLoader);
                    } else {
                        reject(new Error('滴滴地图 Loader 未正确挂载'));
                    }
                };
                script.onerror = () => reject(new Error('滴滴地图 Loader 加载失败'));
                document.head.appendChild(script);
            }).catch((error) => {
                diMapLoaderPromise = null;
                throw error;
            });
            return diMapLoaderPromise;
        },
        initMap(centerArr) {
            if (!this.$refs.mapContainer || this.mapLoading) {
                if (centerArr && centerArr.length === 2) {
                    this.pendingCenter = centerArr;
                }
                return;
            }
            const targetCenter = centerArr && centerArr.length === 2 ? centerArr : DEFAULT_CENTER;
            if (this.map && this.mapLoaded) {
                this.updateCenter(targetCenter);
                return;
            }
            this.mapLoading = true;
            let didiConfig;
            try {
                didiConfig = this.validateDidiConfig();
            } catch (error) {
                this.mapLoading = false;
                console.error('滴滴地图配置缺失:', error);
                return;
            }
            this.ensureDiMapLoader()
                .then((loader) => loader.load({ key: didiConfig.key }))
                .then(({ DiMap }) => {
                    this.DiMap = DiMap;
                    this.map = new DiMap.Map({
                        container: this.$refs.mapContainer,
                        style: 'dimap://styles/normal',
                        zoom: this.zoom - 1 || 18,
                        center: targetCenter
                    });
                    this.map.on('load', () => {
                        this.mapLoaded = true;
                        this.mapLoading = false;
                        const latestCenter = this.pendingCenter || this.normalizeCenter(this.center);
                        this.pendingCenter = null;
                        this.updateCenter(latestCenter);
                        if (this.mapType == 'position') {
                            this.currentMarkers.forEach((marker) => {
                                if (marker && marker.remove) {
                                    marker.remove();
                                }
                            });
                            this.currentMarkers = [];
                            this.addMarker({
                                position: latestCenter
                            });
                        }
                        this.flushPendingPaths();
                        this.onMapLoad(this.map);
                    });
                })
                .catch((error) => {
                    const message = String((error && error.message) || error || '');
                    if (message.includes('status code 902')) {
                        console.error('滴滴地图初始化失败: securityJsCode 缺失或无效，请检查 VUE_APP_DIDI_MAP_SECURITY_CODE', error);
                    } else if (message.includes('INVALID_USER_KEY') || message.includes('status code 10001')) {
                        console.error('滴滴地图初始化失败: key 无效，请检查 VUE_APP_DIDI_MAP_KEY', error);
                    } else {
                        console.error('滴滴地图初始化失败:', error);
                    }
                    this.mapLoading = false;
                });
        },
        updateCenter(centerArr) {
            if (!this.map || !this.mapLoaded) {
                if (centerArr && centerArr.length === 2) {
                    this.pendingCenter = centerArr;
                }
                return;
            }
            this.pendingCenter = null;
            if (this.map.setCenter) {
                this.map.setCenter(centerArr);
            }
            if (this.mapType == 'position') {
                this.currentMarkers.forEach((marker) => {
                    if (marker && marker.remove) {
                        marker.remove();
                    }
                });
                this.currentMarkers = [];
                this.addMarker({
                    position: centerArr
                });
            }
        },
        flushPendingPaths() {
            if (!this.pendingPaths.length) {
                return;
            }
            const paths = this.pendingPaths.slice();
            this.pendingPaths = [];
            paths.forEach((path) => this.addPolyline(path));
        },
        clearOverlays() {
            this.currentPolylines.forEach((polyline) => {
                if (polyline && polyline.remove) {
                    polyline.remove();
                }
            });
            this.currentPolylines = [];
            this.currentMarkers.forEach((marker) => {
                if (marker && marker.remove) {
                    marker.remove();
                }
            });
            this.currentMarkers = [];
        },
        addMarker(markerOptions) {
            if (!this.mapLoaded || !this.map) {
                return;
            }
            const defaultPosition = this.didiMapData && this.didiMapData.lng && this.didiMapData.lat
                ? [this.didiMapData.lng, this.didiMapData.lat]
                : DEFAULT_CENTER;
            const options = Object.assign(
                {
                    position: defaultPosition,
                    iconUrl: require('../../public/images/msi/online/car_icon.png'),
                    iconSize: [24, 44],
                    text: '',
                    offset: [0, 0]
                },
                markerOptions
            );
            if (!options.position || !Array.isArray(options.position) || options.position.length !== 2) {
                return;
            }
            if (!this.DiMap) {
                return;
            }
            const div = document.createElement('div');
            const img = document.createElement('img');
            const text = document.createElement('div');
            div.style.display = 'flex';
            div.style.flexDirection = 'column';
            div.style.alignItems = 'center';
            div.style.justifyContent = 'flex-start';
            div.style.pointerEvents = 'none';
            div.style.display = 'flex';
            img.style.width = options.iconSize[0] + 'px';
            img.style.height = options.iconSize[1] + 'px';
            img.src = options.iconUrl;
            text.textContent = options.text;
            text.style.color = 'black';
            text.style.fontSize = '12px';
            text.style.marginTop = '2px';
            text.style.whiteSpace = 'nowrap';
            div.appendChild(img);
            if (options.text) {
                div.appendChild(text);
            }
            let marker = null;
            try {
                marker = new this.DiMap.Marker({
                    element: div,
                    anchor: 'bottom'
                });
            } catch (error) {
                try {
                    marker = new this.DiMap.Marker(div);
                } catch (fallbackError) {
                    console.error('滴滴地图 marker 创建失败', fallbackError || error);
                    return;
                }
            }
            marker.setLngLat(options.position);
            marker.addTo(this.map);
            this.currentMarkers.push(marker);
            return marker;
        },
        addPolyline(pathData) {
            if (!pathData || !Array.isArray(pathData) || pathData.length < 2) {
                return;
            }
            if (!this.mapLoaded || !this.map) {
                this.pendingPaths.push(pathData);
                return;
            }
            if (!this.DiMap) {
                this.pendingPaths.push(pathData);
                return;
            }
            const polyline = new this.DiMap.Polyline({
                map: this.map,
                path: pathData,
                strokeColor: '#409EFF',
                strokeStyle: 'solid',
                lineCap: 'butt',
                lineJoin: 'miter',
                strokeWeight: 6,
                strokeOpacity: 1,
                borderWeight: 2,
                isOutline: false,
                showDir: true
            });
            polyline.show();
            this.currentPolylines.push(polyline);
            this.fitBoundsToPath(pathData);
            return polyline;
        },
        fitBoundsToPath(pathData) {
            if (!pathData || pathData.length < 2 || !this.map) {
                return;
            }
            try {
                let minLng = Infinity;
                let maxLng = -Infinity;
                let minLat = Infinity;
                let maxLat = -Infinity;
                pathData.forEach((point) => {
                    if (Array.isArray(point) && point.length >= 2) {
                        const lng = Number(point[0]);
                        const lat = Number(point[1]);
                        if (!isNaN(lng) && !isNaN(lat)) {
                            minLng = Math.min(minLng, lng);
                            maxLng = Math.max(maxLng, lng);
                            minLat = Math.min(minLat, lat);
                            maxLat = Math.max(maxLat, lat);
                        }
                    }
                });
                if (minLng === Infinity) {
                    return;
                }
                const pad = 0.1;
                const dLng = (maxLng - minLng) * pad;
                const dLat = (maxLat - minLat) * pad;
                this.map.fitBounds([
                    [minLng - dLng, minLat - dLat],
                    [maxLng + dLng, maxLat + dLat]
                ]);
            } catch (error) {
                console.error('fitBoundsToPath失败', error);
            }
        },
        wgs84ToGcj02(wgsLng, wgsLat) {
            if (wgsLng == null || wgsLat == null || isNaN(Number(wgsLng)) || isNaN(Number(wgsLat))) {
                return { lng: DEFAULT_CENTER[0], lat: DEFAULT_CENTER[1] };
            }
            wgsLng = Number(wgsLng);
            wgsLat = Number(wgsLat);
            const pi = Math.PI;
            const a = 6378245.0;
            const ee = 0.00669342162296594323;
            const outOfChina = (lng, lat) => lng < 72.004 || lng > 137.8347 || lat < 0.8293 || lat > 55.8271;
            if (outOfChina(wgsLng, wgsLat)) {
                return {
                    lng: parseFloat(wgsLng.toFixed(6)),
                    lat: parseFloat(wgsLat.toFixed(6))
                };
            }
            const dLat = this._transformLat(wgsLng - 105.0, wgsLat - 35.0);
            const dLng = this._transformLng(wgsLng - 105.0, wgsLat - 35.0);
            const radLat = (wgsLat / 180.0) * pi;
            let magic = Math.sin(radLat);
            magic = 1 - ee * magic * magic;
            const sqrtMagic = Math.sqrt(magic);
            return {
                lng: parseFloat((wgsLng + (dLng * 180.0) / ((a / sqrtMagic) * Math.cos(radLat) * pi)).toFixed(6)),
                lat: parseFloat((wgsLat + (dLat * 180.0) / (((a * (1 - ee)) / (magic * sqrtMagic)) * pi)).toFixed(6))
            };
        },
        bd09ToGcj02(bdLng, bdLat) {
            if (bdLng == null || bdLat == null || isNaN(Number(bdLng)) || isNaN(Number(bdLat))) {
                return { lng: DEFAULT_CENTER[0], lat: DEFAULT_CENTER[1] };
            }
            const xPi = (Math.PI * 3000.0) / 180.0;
            const x = Number(bdLng) - 0.0065;
            const y = Number(bdLat) - 0.006;
            const z = Math.sqrt(x * x + y * y) - 0.00002 * Math.sin(y * xPi);
            const theta = Math.atan2(y, x) - 0.000003 * Math.cos(x * xPi);
            return {
                lng: parseFloat((z * Math.cos(theta)).toFixed(6)),
                lat: parseFloat((z * Math.sin(theta)).toFixed(6))
            };
        },
        _transformLat(x, y) {
            const pi = Math.PI;
            let r = -100.0 + 2.0 * x + 3.0 * y + 0.2 * y * y + 0.1 * x * y + 0.2 * Math.sqrt(Math.abs(x));
            r += ((20.0 * Math.sin(6.0 * x * pi) + 20.0 * Math.sin(2.0 * x * pi)) * 2.0) / 3.0;
            r += ((20.0 * Math.sin(y * pi) + 40.0 * Math.sin((y / 3.0) * pi)) * 2.0) / 3.0;
            r += ((160.0 * Math.sin((y / 12.0) * pi) + 320 * Math.sin((y * pi) / 30.0)) * 2.0) / 3.0;
            return r;
        },
        _transformLng(x, y) {
            const pi = Math.PI;
            let r = 300.0 + x + 2.0 * y + 0.1 * x * x + 0.1 * x * y + 0.1 * Math.sqrt(Math.abs(x));
            r += ((20.0 * Math.sin(6.0 * x * pi) + 20.0 * Math.sin(2.0 * x * pi)) * 2.0) / 3.0;
            r += ((20.0 * Math.sin(x * pi) + 40.0 * Math.sin((x / 3.0) * pi)) * 2.0) / 3.0;
            r += ((150.0 * Math.sin((x / 12.0) * pi) + 300.0 * Math.sin((x / 30.0) * pi)) * 2.0) / 3.0;
            return r;
        }
    },
    watch: {
        center: {
            handler(val) {
                const center = this.normalizeCenter(val);
                this.initMap(center);
            },
            immediate: true
        }
    }
};
</script>

<style scoped>
.map-container {
    width: 100%;
    height: 100%;
}
</style>
